import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
  GOOGLE_MAPS_VERSION,
} from "../utils/googleMapsLoader";
import PlacesNewAutocomplete from "./PlacesNewAutocomplete";

/**
 * Places availability (bug 26).
 *
 * Station/address search uses Places API (New) REST — not the legacy Maps
 * Autocomplete widget. The map still loads via Maps JS; search fails soft to
 * the pin flow if the key is missing or Google auth fails.
 */
let placesAuthFailed = false;
const placesAuthSubscribers = new Set<() => void>();

function installAuthFailureHook() {
  if (typeof window === "undefined") return;
  const w = window as unknown as { gm_authFailure?: () => void; __pearGmAuthHooked?: boolean };
  if (w.__pearGmAuthHooked) return;
  w.__pearGmAuthHooked = true;
  const previous = w.gm_authFailure;
  w.gm_authFailure = () => {
    placesAuthFailed = true;
    placesAuthSubscribers.forEach((notify) => notify());
    if (typeof previous === "function") previous();
  };
}

export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Reusable map + draggable-pin location picker.
 *
 * WHY a confirmed pin instead of saving a raw Google Places result: Google's
 * Maps Platform terms forbid permanently storing Places lat/lng (30-day cache
 * limit; only `place_id` is storable long-term). A user-placed pin is a
 * user-declared coordinate we may keep forever, it works offline for geofencing
 * with no per-use API call, and — because the user drops it on the actual gate —
 * it is usually more accurate than a raw search result.
 *
 * Type-to-search (Google Places Autocomplete) is ON by default now that Places is
 * enabled. Pass `searchEnabled={false}` to force pin-only. The map + draggable pin
 * still works on its own, so this degrades gracefully if Places is unavailable.
 */
const NAIROBI: LatLng = { lat: -1.286389, lng: 36.817223 };

interface Props {
  /** Current pin, or null when nothing has been placed yet. */
  value: LatLng | null;
  onChange: (loc: LatLng) => void;
  label?: string;
  heightPx?: number;
  /** Type-to-search (Places Autocomplete). On by default; set false for pin-only. */
  searchEnabled?: boolean;
  /* CONTROLLED SEARCH TEXT — one field for two jobs.
     ────────────────────────────────────────────────────────────────────────
     The school-trip composer used to show a free-text "Destination" box AND
     this picker's own search box, stacked. Gabriel: "it should just be
     DESTINATION, then under it the bar for searching the location. Not two
     bars, that's pointless." He is right — they ask the same question, and
     two boxes let them disagree about where the trip is going.

     When the caller passes these, IT owns the text: typing updates the
     caller's field (free), and picking a suggestion drops the pin as well.
     Every other call site omits them and keeps its own internal state. */
  searchValue?: string;
  onSearchValueChange?: (text: string) => void;
  searchPlaceholder?: string;
  /** CSS class for the search input (default "tc-input"). */
  inputClassName?: string;
}

export default function LocationPicker({
  value,
  onChange,
  label,
  heightPx = 260,
  searchEnabled = true,
  searchValue,
  onSearchValueChange,
  searchPlaceholder,
  inputClassName,
}: Props) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  // Centre the map ONCE (edit mode centres on the existing pin; create mode on
  // Nairobi). Deliberately not tied to `value` so dragging the pin never yanks
  // the map back.
  const [initialCenter] = useState<LatLng>(() => value ?? NAIROBI);

  // Imperative map handle so a search result can pan/zoom the map without making
  // `center` a controlled prop (which would fight the user's own panning).
  const mapRef = useRef<google.maps.Map | null>(null);
  const [ownSearchText, setOwnSearchText] = useState("");
  // Controlled when the caller supplies both; uncontrolled otherwise.
  const searchText = searchValue ?? ownSearchText;
  const setSearchText = onSearchValueChange ?? setOwnSearchText;

  // Whether type-to-search can actually work right now.
  const [placesBlocked, setPlacesBlocked] = useState(placesAuthFailed);
  useEffect(() => {
    installAuthFailureHook();
    const notify = () => setPlacesBlocked(true);
    placesAuthSubscribers.add(notify);
    return () => { placesAuthSubscribers.delete(notify); };
  }, []);

  // Hint under the search box — covers "typed but selected nothing", which used to
  // fail completely silently.
  const [searchHint, setSearchHint] = useState<string | null>(null);

  const onSearchPick = useCallback((p: { name: string; placeId: string; lat?: number; lng?: number }) => {
    setSearchText(p.name);
    if (p.lat == null || p.lng == null || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) {
      setSearchHint("Pick a suggestion with a map pin, or click the map where the gate is.");
      return;
    }
    setSearchHint(null);
    const pin = { lat: p.lat, lng: p.lng };
    onChange(pin);
    mapRef.current?.panTo(pin);
    mapRef.current?.setZoom(16);
    // setSearchText is either the caller's setter or useState's — both stable
    // in practice, but listed so the hook rule is satisfied honestly rather
    // than silenced.
  }, [onChange, setSearchText]);

  /* A manual pin IS the answer — so it must also fill the text box the form
     validates on. Dropping a pin with an empty box used to refuse the save
     ("because I didn't write the location it still refused" — Gabriel,
     09-04), the mirror-image of typing filling the pin.

     Rules the review forced on the first cut:
     - NEVER overwrite text a human typed — the box is the saved destination
       name; only an empty box, or our own previous auto-label, is fair game.
     - Fill the coordinates fallback SYNCHRONOUSLY, so a click-then-Save has
       a valid destination even before (or without) Google answering — a
       Places-New-only browser key rejects the legacy Geocoder entirely.
     - The reverse geocode (💸 ~KES 0.65) is debounced to one call per
       settled pin, request-stamped so a slow response can't label a pin the
       user already moved, and reuses one Geocoder instance. */
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const labelReqId = useRef(0);
  const lastAutoLabel = useRef<string | null>(null);
  const labelTimer = useRef<number | null>(null);

  const labelPin = useCallback((pin: LatLng) => {
    const current = (searchText ?? "").trim();
    if (current && current !== lastAutoLabel.current) return;
    const fallback = `Pinned location (${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)})`;
    lastAutoLabel.current = fallback;
    setSearchText(fallback);
    setSearchHint(null);
    const id = ++labelReqId.current;
    if (labelTimer.current) window.clearTimeout(labelTimer.current);
    labelTimer.current = window.setTimeout(() => {
      try {
        geocoderRef.current ??= new google.maps.Geocoder();
        geocoderRef.current.geocode({ location: pin }, (results, status) => {
          if (id !== labelReqId.current) return;
          const name = status === "OK" && results && results[0]?.formatted_address;
          if (!name) return; // honest coordinates stand
          lastAutoLabel.current = name;
          setSearchText(name);
        });
      } catch { /* fallback is already in the box */ }
    }, 600);
  }, [searchText, setSearchText]);

  /* One handler for map click AND marker drag — the two bodies had already
     diverged once. */
  const setPin = useCallback((ll: google.maps.LatLng | null) => {
    if (!ll) return;
    const pin = { lat: ll.lat(), lng: ll.lng() };
    onChange(pin);
    labelPin(pin);
  }, [onChange, labelPin]);

  const pick = useCallback(
    (e: google.maps.MapMouseEvent) => setPin(e.latLng),
    [setPin],
  );

  // "Use my location" — the fast path when the person filling this in is
  // standing at the place (admin at the school, parent at their own gate).
  // Typing an address is slower and lands on a road, not a gate.
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError("This browser cannot share a location. Drop the pin by hand.");
      return;
    }
    setLocating(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const pin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        onChange(pin);
        labelPin(pin);
        mapRef.current?.panTo(pin);
        // Gate-level zoom: at 12 you are choosing an estate, at 17 a gate.
        mapRef.current?.setZoom(17);
      },
      () => {
        setLocating(false);
        setGeoError("Could not get your location. Drop the pin on the map instead.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  }, [onChange, labelPin]);

  const sub: React.CSSProperties = {
    fontSize: "12px",
    color: "var(--t2, #667)",
    marginTop: "6px",
  };

  /* A DEAD MAP MUST NOT TAKE THE TEXT FIELD DOWN WITH IT.
     ────────────────────────────────────────────────────────────────────────
     When the caller owns the search text, this component IS their text field —
     the school-trip composer no longer has a separate Destination input. These
     three early returns render only a message, so a missing API key, a blocked
     Places call or a slow script load would leave the admin with nowhere to
     type a destination at all, and the trip could not be created. That is a
     far worse failure than losing the pin.

     Uncontrolled callers keep the original behaviour exactly. */
  const textOnlyFallback = (note: string) => (
    <div>
      <input
        className={inputClassName ?? "tc-input"}
        style={{ width: "100%" }}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder={searchPlaceholder ?? "Type the location"}
      />
      <div style={{ ...sub, marginTop: 6 }}>{note}</div>
    </div>
  );
  const callerOwnsText = typeof onSearchValueChange === "function";

  if (!apiKey) {
    return callerOwnsText
      ? textOnlyFallback("Map search is off — set REACT_APP_GOOGLE_MAPS_API_KEY to drop a pin as well.")
      : (
        <div style={sub}>
          Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to place a location on the map.
        </div>
      );
  }
  if (loadError) {
    return callerOwnsText
      ? textOnlyFallback("The map failed to load — you can still type the location, just without a pin.")
      : <div style={sub}>Map failed to load.</div>;
  }
  if (!isLoaded) {
    return callerOwnsText
      ? textOnlyFallback("Loading the map…")
      : <div style={sub}>Loading map…</div>;
  }

  // Search is REST Places (New) — only needs the API key, not the JS places lib.
  const searchAvailable = searchEnabled && !placesBlocked && Boolean(apiKey);

  return (
    <div>
      {label && (
        <label
          style={{
            fontSize: "12px",
            fontWeight: 600,
            color: "var(--t2, #667)",
            display: "block",
            marginBottom: "6px",
          }}
        >
          {label}
        </label>
      )}

      {/* Type-to-search via Places API (New) REST. Restricted to Kenya. */}
      {searchAvailable ? (
        <PlacesNewAutocomplete
          apiKey={apiKey}
          value={searchText}
          onChange={setSearchText}
          onPick={onSearchPick}
          placeholder={searchPlaceholder ?? "Search for the location…"}
          regionCodes={["ke"]}
          wantLocation
          className="tc-input"
          inputStyle={{ width: "100%", marginBottom: "8px" }}
        />
      ) : (
        <input
          className="tc-input"
          disabled
          placeholder={
            searchEnabled
              ? "Address search unavailable — click the map instead"
              : "Drag the pin to the exact spot (type-to-search turns on with Google Places)"
          }
          style={{ width: "100%", marginBottom: "8px", opacity: 0.7 }}
        />
      )}

      {/* Say WHY search is dead. Without this the only signal is Google's overlay
          across the map, which reads as "the map is broken" — it isn't. */}
      {/* Same reasoning as textOnlyFallback: when the caller owns the text, the
          warning below is not enough — they need somewhere to type. */}
      {searchEnabled && !searchAvailable && typeof onSearchValueChange === "function" && (
        <input
          className={inputClassName ?? "tc-input"}
          style={{ width: "100%", marginBottom: "8px" }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={searchPlaceholder ?? "Type the location"}
        />
      )}
      {searchEnabled && !searchAvailable && (
        <div style={{ ...sub, marginTop: 0, marginBottom: 8, color: "#b45309" }}>
          Address search is unavailable — set REACT_APP_GOOGLE_MAPS_API_KEY with
          Places API (New), or click the map / use your location to drop the pin.
          A pin you place by hand is more accurate than a search result anyway.
        </div>
      )}
      {searchHint && (
        <div style={{ ...sub, marginTop: 0, marginBottom: 8, color: "#b45309" }}>{searchHint}</div>
      )}

      <div style={{ position: "relative" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: `${heightPx}px`, borderRadius: "10px" }}
          center={initialCenter}
          zoom={value ? 16 : 12}
          onClick={pick}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {value && (
            <Marker
              position={value}
              draggable
              onDragEnd={(e) => setPin(e.latLng)}
            />
          )}
        </GoogleMap>

        {/* Nothing placed yet: put the instruction on the map, where the eye
            already is, rather than in grey text underneath it. */}
        {!value && (
          <div
            style={{
              position: "absolute", top: 12, left: 12, right: 12,
              background: "rgba(17,17,17,0.72)", color: "#fff",
              fontSize: 12.5, fontWeight: 600, textAlign: "center",
              padding: "8px 10px", borderRadius: 8, pointerEvents: "none",
            }}
          >
            Click the map where the gate is
          </div>
        )}

        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating}
          style={{
            position: "absolute", left: 10, bottom: 10,
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "#fff", border: "1px solid #d8dee9", borderRadius: 20,
            padding: "7px 12px", fontSize: 12.5, fontWeight: 600,
            color: "#1c1730", cursor: locating ? "default" : "pointer",
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)", opacity: locating ? 0.75 : 1,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
          </svg>
          {locating ? "Locating…" : "Use my location"}
        </button>
      </div>

      {/* Status. Nobody verifies a pin by reading coordinates — lead with words,
          keep the numbers as small print underneath. */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 8 }}>
        <span style={{ color: value ? "#16a34a" : "#94a3b8", lineHeight: "16px", flexShrink: 0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            {value ? <><circle cx="12" cy="12" r="10" /><path d="M8 12.5l2.5 2.5L16 9.5" /></> : <><circle cx="12" cy="12" r="10" /><path d="M12 8v5M12 16h.01" /></>}
          </svg>
        </span>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: value ? "#15803d" : "var(--t2, #667)" }}>
            {value
              ? "Location pinned — drag the pin if it is not exactly right."
              : "No pin yet. Click the map, search above, or use your location."}
          </div>
          {value && (
            <div style={{ fontSize: 11.5, color: "var(--t2, #667)" }}>
              {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
            </div>
          )}
          {geoError && (
            <div style={{ fontSize: 11.5, color: "#b45309", marginTop: 2 }}>{geoError}</div>
          )}
        </div>
      </div>
    </div>
  );
}
